import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Observable, of, throwError } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ObjectApiInterface } from './interfaces/object-api.interface';
import { filter, map, take, switchMap, catchError } from 'rxjs/operators';
import { CleanCollectionFieldsModel } from '../models/clean-collection-fields.model';
import { DEFAULT_PAGE_SIZE } from '../config/base-table.config';
import { Model } from '../models/model';
import { AuthService } from '../services/auth.service';
import { ENUM_PERMISSIONS } from '../enums/permission.enum';

@Injectable({
  providedIn: 'root',
})
export class BaseService<T> {
  protected _entity = new BehaviorSubject<T>(null);
  readonly entity$ = this._entity.asObservable().pipe(filter(entity => !!entity));

  defaultSort = 'createdAt';
  defaultSortOrder = 'desc';
  entityApi: string;

  baseUrlApi: string;

  constructor(
    public http: HttpClient,
    @Inject('entityApi') entityApi: string = '',
    public authService?: AuthService,
  ) {
    this.entityApi = entityApi;
    this.baseUrlApi = `/api/${this.entityApi}`;
  }

  get entity() {
    return lastValueFrom(this.entity$.pipe(take(1)));
  }

  resetEntity() {
    this._entity.next(null);
  }

  setEntity(entity: T) {
    this._entity.next(entity);
  }

  refreshEntity() {
    this._entity.next(this._entity.value);
  }

  deleteFields(data: CleanCollectionFieldsModel): Observable<ObjectApiInterface<T>> {
    return this.http.put<ObjectApiInterface<T>>(`${this.baseUrlApi}/deleteEmptyFields`, data);
  }

  /**
   * Find an entity by its ID and update the internal entity state.
   * @param id The ID of the entity to find.
   * @returns An Observable that emits the found entity wrapped in an ObjectApiInterface.
   */
  findOneById(id: string): Observable<ObjectApiInterface<T>> {
    return this.http.get<ObjectApiInterface<T>>(`${this.baseUrlApi}/get/${id}`).pipe(
      map(x => {
        this._entity.next(x.data);
        return x;
      }),
    );
  }

  findAll(
    page?: number,
    perPage?: number | 'all',
    sortField: string = this.defaultSort,
    sortOrder: string = this.defaultSortOrder,
    search?: string,
    filters?: T | any,
  ): Observable<ObjectApiInterface<T[]>> {
    return this.http.get<ObjectApiInterface<T[]>>(`${this.baseUrlApi}/list`, {
      params: new HttpParams()
        .set('page', page?.toString() ?? '1')
        .set('perPage', perPage?.toString() ?? DEFAULT_PAGE_SIZE.toString())
        .set('sort', `${sortField}@${sortOrder}`)
        .set('search', search ?? '')
        .set('filters', JSON.stringify(filters) ?? ''),
    });
  }

  /** Résout des entités par _id (chips de filtres, PRED-3054) : GET /list?ids=… (BaseController.findAll). Nommé ainsi car `findByIds` existe déjà avec d'autres signatures dans des services enfants. */
  resolveByIds(ids: string[]): Observable<T[]> {
    if (!ids?.length) {
      return of([]);
    }
    return this.http
      .get<ObjectApiInterface<T[]>>(`${this.baseUrlApi}/list`, {
        params: new HttpParams()
          .set('page', '1')
          .set('perPage', ids.length.toString())
          .set('sort', `${this.defaultSort}@${this.defaultSortOrder}`)
          .set('ids', JSON.stringify(ids)),
      })
      .pipe(map(res => res.data ?? []));
  }

  getOrderProductsPrestations(
    page?: number,
    perPage?: number,
    sortField: string = this.defaultSort,
    sortOrder: string = 'desc',
    search?: string,
    filters?: T | any,
    order?: T | any,
    typerEntity?: any,
  ): Observable<ObjectApiInterface<T[]>> {
    return this.http.get<ObjectApiInterface<T[]>>(
      `${this.baseUrlApi}/getOrderProductsPrestations/${order}/${typerEntity}`,
      {
        params: new HttpParams()
          .set('page', page?.toString() ?? '1')
          .set('perPage', perPage?.toString() ?? '500')
          .set('sort', `${sortField}@${sortOrder}`)
          .set('search', search ?? '')
          .set('filters', JSON.stringify(filters) ?? ''),
      },
    );
  }

  count(search?: string, filters?: T | any): Observable<ObjectApiInterface<any>> {
    return this.http.get<ObjectApiInterface<any>>(`${this.baseUrlApi}/count`, {
      params: new HttpParams().set('search', search ?? '').set('filters', JSON.stringify(filters) ?? ''),
    });
  }

  findById(id: string): Observable<ObjectApiInterface<T>> {
    return this.http.get<ObjectApiInterface<T>>(`${this.baseUrlApi}/get/${id}`).pipe(
      map(x => {
        this._entity.next(x.data);
        return x;
      }),
    );
  }

  create(entity): Observable<ObjectApiInterface<T>> {
    return this.http.post<ObjectApiInterface<T>>(`${this.baseUrlApi}/create`, entity, {
      headers: { 'Error-CRUD': 'CREATE' },
    });
  }

  import(entity: T): Observable<ObjectApiInterface<T>> {
    return this.http.post<ObjectApiInterface<T>>(`${this.baseUrlApi}/import`, entity, {
      headers: { 'Error-CRUD': 'IMPORT' },
    });
  }

  downloadExcelTemplate(): Observable<ObjectApiInterface<T>> {
    return this.http.get<ObjectApiInterface<T>>(`${this.baseUrlApi}/download-excel-template`);
  }

  // Lance la synchronisation en arrière-plan : started vaut false si une synchronisation tourne déjà.
  synchronizeDocuware(): Observable<{ started: boolean }> {
    return this.http.get<{ started: boolean }>(`${this.baseUrlApi}/docuware/synchronize`);
  }

  update(entity: T | any): Observable<ObjectApiInterface<T>> {
    if (entity.order) {
      entity.order = {
        _id: entity.order._id,
        technicalIdentifier: entity.order.technicalIdentifier,
        created_at: entity.created_at,
      };
    }

    return this.http
      .put<ObjectApiInterface<T>>(`${this.baseUrlApi}/update/${entity['_id']}`, entity, {
        headers: { 'Error-CRUD': 'UPDATE' },
      })
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  delete(entity: T): Observable<ObjectApiInterface<T>> {
    return this.http.delete<ObjectApiInterface<T>>(`${this.baseUrlApi}/delete/${entity['_id']}`, {
      headers: { 'Error-CRUD': 'DELETE' },
    });
  }

  switchIsActive(entity: T) {
    return this.http.get(`${this.baseUrlApi}/switchIsActive/${entity['_id']}`);
  }

  getResume(id: string): Observable<ObjectApiInterface<T>> {
    return this.http.get<ObjectApiInterface<T>>(`${this.baseUrlApi}/resume/${id}`);
  }

  searchAutocomplete(observableSearch: Observable<T[]>, search?: string): Observable<T[]> {
    observableSearch = this.http
      .get<ObjectApiInterface<T[]>>(`${this.baseUrlApi}/list`, {
        params: new HttpParams()
          .set('page', '1')
          .set('perPage', '50')
          .set('search', search ?? ''),
      })
      .pipe(map(d => d.data));
    return observableSearch;
  }

  archive(entity: T): Observable<ObjectApiInterface<T>> {
    return this.http.get<ObjectApiInterface<T>>(`${this.baseUrlApi}/archive/${entity['_id']}`);
  }

  unarchive(entity: T): Observable<ObjectApiInterface<T>> {
    return this.http.get<ObjectApiInterface<T>>(`${this.baseUrlApi}/unarchive/${entity['_id']}`);
  }

  suspend(entity: T): Observable<ObjectApiInterface<T>> {
    return this.http.get<ObjectApiInterface<T>>(`${this.baseUrlApi}/suspend/${entity['_id']}`);
  }

  unsuspend(entity: T): Observable<ObjectApiInterface<T>> {
    return this.http.get<ObjectApiInterface<T>>(`${this.baseUrlApi}/unsuspend/${entity['_id']}`);
  }

  checkDependencies(model: Model): Observable<ObjectApiInterface<T>> {
    return this.http.get<ObjectApiInterface<T>>(`${this.baseUrlApi}/checkDependencies/${model._id}`);
  }

  /**
   * Exécute une opération (appel API) uniquement si l'utilisateur a les permissions requises
   * @param requiredPermissions - Liste des permissions nécessaires (au moins une suffit)
   * @param operationFactory - Factory function qui retourne l'Observable de l'opération
   * @param defaultValue - Valeur par défaut retournée si l'utilisateur n'a pas les permissions
   * @returns Observable contenant le résultat de l'opération ou la valeur par défaut
   *
   * @example
   * // Dans un composant
   * this.billService.executeWithPermission(
   *     [ENUM_PERMISSIONS.READ_BILL],
   *     () => this.billService.findAll(null, null, null, null, null, {orderId: orderId}),
   *     {data: [], totalData: 0}
   * ).subscribe(bills => {
   *     // Traiter les factures (ou valeur par défaut si pas de permission)
   * });
   */
  executeWithPermission<R>(
    requiredPermissions: ENUM_PERMISSIONS[],
    operationFactory: () => Observable<R>,
    defaultValue?: R,
  ): Observable<R | null> {
    // Vérifier que AuthService est injecté
    if (!this.authService) {
      console.error('AuthService non injecté dans BaseService. Impossible de vérifier les permissions.');
      return throwError(() => new Error('AuthService non disponible'));
    }

    return this.authService.getCurrentUserPermissions$().pipe(
      take(1), // On ne prend que la valeur actuelle des permissions
      switchMap((userPermissions: ENUM_PERMISSIONS[]) => {
        // Vérifier si l'utilisateur a AU MOINS UNE des permissions requises
        const hasPermission = requiredPermissions.some(perm => userPermissions.includes(perm));

        if (hasPermission) {
          console.log(`✅ Permission validée pour: ${requiredPermissions.join(', ')}`);
          // Exécuter l'opération SEULEMENT si permission OK
          return operationFactory();
        } else {
          console.warn(
            `⚠️ Permissions manquantes: ${requiredPermissions.join(', ')}. Utilisateur possède: ${userPermissions.join(', ')}`,
          );
          // Retourner la valeur par défaut SANS appeler le backend
          return of(defaultValue ?? null);
        }
      }),
      catchError(error => {
        console.error("Erreur lors de la vérification des permissions ou de l'exécution de l'opération:", error);
        return of(defaultValue ?? null);
      }),
    );
  }

  /**
   * Variante stricte : Lance une erreur si l'utilisateur n'a pas les permissions
   * Utile pour les opérations critiques où on veut un feedback explicite
   * @param requiredPermissions - Liste des permissions nécessaires (au moins une suffit)
   * @param operationFactory - Factory function qui retourne l'Observable de l'opération
   * @returns Observable contenant le résultat de l'opération ou une erreur
   *
   * @example
   * this.orderService.executeWithPermissionStrict(
   *     [ENUM_PERMISSIONS.UPDATE_ORDER],
   *     () => this.orderService.update(order)
   * ).subscribe(result => console.log(result), error => console.error(error));
   */
  executeWithPermissionStrict<R>(
    requiredPermissions: ENUM_PERMISSIONS[],
    operationFactory: () => Observable<R>,
  ): Observable<R> {
    if (!this.authService) {
      return throwError(() => new Error('AuthService non disponible'));
    }

    return this.authService.getCurrentUserPermissions$().pipe(
      take(1),
      switchMap((userPermissions: ENUM_PERMISSIONS[]) => {
        const hasPermission = requiredPermissions.some(perm => userPermissions.includes(perm));

        if (hasPermission) {
          return operationFactory();
        } else {
          // Lance une erreur explicite
          return throwError(() => ({
            status: 403,
            message: `Permissions insuffisantes. Requis: ${requiredPermissions.join(', ')}`,
            requiredPermissions,
            userPermissions,
          }));
        }
      }),
    );
  }
}
